/**
 * Full-text search helpers for the Learning Library (Beta-1 plan P5-08, Tim D09 §1). Pure module.
 *
 * Client-side at beta scale: the library holds tens of documents, so we search the case-study
 * fields we already hold (title, summary, key findings, lessons, tags) plus the extracted
 * `Hugo_DocumentText.fullText` cache the ingest workflow writes. Terms are matched
 * case-insensitively; a quoted query ("texas city") is matched as a phrase, otherwise every term
 * must appear somewhere in the document (AND).
 */

export const SNIPPET_RADIUS = 80;
export const MAX_SNIPPETS = 3;

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Filler words dropped from a query unless the whole query is filler ("log an OE condition" → log, oe, condition). */
export const STOPWORDS = new Set(['a', 'an', 'the', 'of', 'to', 'in', 'on', 'at', 'for', 'and', 'or', 'is', 'it', 'my', 'me', 'i', 'we', 'our', 'do', 'how', 'what', 'with', 'from', 'by', 'as', 'be', 'that', 'this']);

/** Parse a query into terms. Quoted text is kept as one phrase; single letters and filler words are dropped. */
export function parseQuery(query) {
  const q = String(query || '').trim();
  if (!q) return [];
  const terms = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m;
  while ((m = re.exec(q))) {
    const t = (m[1] || m[2] || '').trim().toLowerCase().replace(/[?!.,;:]+$/g, '');
    if (t.length >= 2) terms.push(t);
  }
  const kept = terms.filter((t) => !STOPWORDS.has(t));
  return kept.length ? kept : terms;
}

/** A regex that matches any of the terms (case-insensitive, global). Null when no terms. */
export function termRegex(terms) {
  if (!terms || !terms.length) return null;
  return new RegExp(terms.map(escapeRegExp).join('|'), 'gi');
}

/** Text fields of a case study worth searching (title, summary, findings, lessons, tags, company, location). */
export function caseStudyText(cs) {
  if (!cs) return '';
  const parts = [cs.title, cs.summary, cs.company, cs.location, cs.industry];
  const push = (v) => {
    if (!v) return;
    if (typeof v === 'string') parts.push(v);
    else if (Array.isArray(v)) v.forEach(push);
    else if (typeof v === 'object') parts.push(v.finding || v.text || v.description || v.lesson || '');
  };
  push(cs.keyFindings);
  push(cs.lessonsLearned);
  push(cs.tags);
  return parts.filter(Boolean).join('\n');
}

/** True when every term appears in the text. */
export function matchesAll(text, terms) {
  if (!terms || !terms.length) return true;
  const hay = String(text || '').toLowerCase();
  return terms.every((t) => hay.includes(t));
}

/** Which terms appear in the text. */
export function termsFound(text, terms) {
  const hay = String(text || '').toLowerCase();
  return (terms || []).filter((t) => hay.includes(t));
}

/**
 * Snippets around the first matches: [{ before, match, after }]. At most `max` snippets, each
 * `radius` characters either side, snapped to word boundaries.
 */
export function snippetsFor(text, terms, { radius = SNIPPET_RADIUS, max = MAX_SNIPPETS } = {}) {
  const src = String(text || '');
  const re = termRegex(terms);
  if (!re || !src) return [];
  const out = [];
  let lastEnd = -1;
  let m;
  while ((m = re.exec(src)) && out.length < max) {
    if (m.index < lastEnd) continue; // inside the previous snippet
    let start = Math.max(0, m.index - radius);
    let end = Math.min(src.length, m.index + m[0].length + radius);
    if (start > 0) { const sp = src.lastIndexOf(' ', start); if (sp > start - 20 && sp >= 0) start = sp + 1; }
    if (end < src.length) { const sp = src.indexOf(' ', end); if (sp !== -1 && sp < end + 20) end = sp; }
    out.push({
      before: (start > 0 ? '…' : '') + src.slice(start, m.index).replace(/\s+/g, ' '),
      match: m[0],
      after: src.slice(m.index + m[0].length, end).replace(/\s+/g, ' ') + (end < src.length ? '…' : ''),
    });
    lastEnd = end;
  }
  return out;
}

/** Split text into [{ text, hit }] segments so a renderer can wrap hits in <mark>. */
export function highlightSegments(text, terms) {
  const src = String(text || '');
  const re = termRegex(terms);
  if (!re || !src) return [{ text: src, hit: false }];
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(src))) {
    if (m.index > last) out.push({ text: src.slice(last, m.index), hit: false });
    out.push({ text: m[0], hit: true });
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex += 1;
  }
  if (last < src.length) out.push({ text: src.slice(last), hit: false });
  return out;
}

/**
 * Search case studies. `fullTextById` maps case-study id → extracted fullText (may be partial —
 * a study whose text is not loaded is still matched on its own fields).
 * @returns {Array<{ caseStudy, where: 'fields'|'document', snippets }>} in the input order
 */
export function searchCaseStudies(caseStudies, query, fullTextById = {}) {
  const terms = parseQuery(query);
  if (!terms.length) return (caseStudies || []).map((cs) => ({ caseStudy: cs, where: null, snippets: [] }));
  const out = [];
  for (const cs of caseStudies || []) {
    const fields = caseStudyText(cs);
    const full = fullTextById[cs.id] || '';
    if (matchesAll(fields, terms)) {
      out.push({ caseStudy: cs, where: 'fields', snippets: snippetsFor(cs.summary || fields, terms, { max: 1 }) });
    } else if (full && matchesAll(`${fields}\n${full}`, terms)) {
      out.push({ caseStudy: cs, where: 'document', snippets: snippetsFor(full, terms) });
    }
  }
  return out;
}
